/**
 * Auth — session endpoints.
 */

import type { MeResponse, UserResponse } from '@/types/auth'
import { apiClient } from './client'

export const auth = {
  /**
   * One-time interactive-sign-in handshake.
   * Called ONLY from initMsal() after handleRedirectPromise() returns a
   * result — never on silent renewal. Records last_login_at and emits
   * auth_user_signed_in on the backend; returns the authoritative
   * UserResponse (id/email/role/etc.) for the SPA to render.
   */
  getSession: async (): Promise<UserResponse> => {
    const response = await apiClient.get<UserResponse>('/api/v1/auth/session/')
    return response.data
  },

  /**
   * Sign-out audit call (ordering: DELETE → await 204 → socket
   * disconnect → logoutRedirect). Must complete while the user is still
   * authenticated so the backend can emit auth_user_signed_out.
   */
  deleteSession: async (): Promise<void> => {
    await apiClient.delete('/api/v1/auth/session/')
  },

  /**
   * Authoritative identity + role for the currently signed-in user.
   * Sourced from the cc_sec_users row — the live DB tier, never the JWT claim.
   * Returns MeResponse (UserResponse + effective_grants) so the SPA can
   * render grant-aware UI without a separate call.
   * Used by CurrentUserContext for staleTime:Infinity + WS-invalidation pattern;
   * do NOT call this on every render — let TanStack Query own the lifecycle.
   */
  getMe: async (): Promise<MeResponse> => {
    const response = await apiClient.get<MeResponse>('/api/v1/auth/me/')
    return response.data
  },
}
